/**
 * Luma Nuit — product page helpers.
 *
 * <ln-format-taille>: "1 panneau / Lot de 2 panneaux" pills. The size list only shows the sizes
 * of the current format, so switching format selects the matching size in the size <select>
 * (adding the option if needed) and lets <variant-picker> handle the change as usual.
 *
 * <ln-guide-tailles>: opens the size guide link in a side <dialog>; the link still works without JS.
 */

class LnFormatTaille extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    // The pills are not a product option: <variant-picker> only needs the size <select> change.
    event.stopPropagation();

    const select = document.getElementById(input.dataset.selectId || '');
    if (!(select instanceof HTMLSelectElement)) return;

    let option = Array.from(select.options).find((item) => item.value === input.value);
    if (!option) {
      option = new Option(input.value, input.value);
      option.hidden = true;
      for (const key of ['inputId', 'optionName', 'optionValueId', 'variantId', 'connectedProductUrl']) {
        const value = input.dataset[key];
        if (value !== undefined) option.dataset[key] = value;
      }
      select.append(option);
    }

    select.value = option.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };
}

class LnGuideTailles extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const link = this.querySelector('[data-ln-guide-ouvrir]');
    const dialog = this.querySelector('dialog');
    if (!(link instanceof HTMLAnchorElement) || !(dialog instanceof HTMLDialogElement)) return;

    link.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        dialog.showModal();
      },
      { signal }
    );

    for (const button of this.querySelectorAll('[data-ln-guide-fermer]')) {
      button.addEventListener('click', () => dialog.close(), { signal });
    }

    // Close when clicking the backdrop.
    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) dialog.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }
}

if (!customElements.get('ln-format-taille')) customElements.define('ln-format-taille', LnFormatTaille);
if (!customElements.get('ln-guide-tailles')) customElements.define('ln-guide-tailles', LnGuideTailles);
